import { ForecastWidgetConfig, WidgetRenderContext, WidgetRenderer } from '../types';
import store from '../../../../state';
import { poiModal } from '../../PoiModal';
import { appendAuthToken } from '../../../../config';
import authService from '../../../../services/AuthService';

interface ProcessedFire {
  name: string;
  acres: number;
  containment: string | number;
  startDate: string;
  updatedDate: string;
  incidentType: string;
  lat: number;
  lon: number;
  distanceMiles: number;
  bearing: string;
}

let cachedWildfiresGeoJson: any = null;
let lastWildfiresFetchTime = 0;

function haversineDistanceMiles(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 3958.8; // Earth radius in miles
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function getBearingLabel(lat1: number, lon1: number, lat2: number, lon2: number): string {
  const phi1 = (lat1 * Math.PI) / 180;
  const phi2 = (lat2 * Math.PI) / 180;
  const deltaLambda = ((lon2 - lon1) * Math.PI) / 180;
  const y = Math.sin(deltaLambda) * Math.cos(phi2);
  const x = Math.cos(phi1) * Math.sin(phi2) - Math.sin(phi1) * Math.cos(phi2) * Math.cos(deltaLambda);
  const bearing = ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
  const directions = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
  const idx = Math.floor((bearing + 11.25) / 22.5) % 16;
  return directions[idx];
}

function getCentroid(geometry: any): [number, number] | null {
  if (!geometry || !geometry.coordinates) return null;
  const type = geometry.type;

  if (type === 'Point') {
    return [geometry.coordinates[1], geometry.coordinates[0]];
  }

  let coords: any[] = [];
  if (type === 'Polygon') {
    coords = geometry.coordinates[0] || [];
  } else if (type === 'MultiPolygon') {
    coords = (geometry.coordinates[0] && geometry.coordinates[0][0]) || [];
  }

  if (!coords.length) return null;

  let sumLat = 0;
  let sumLon = 0;
  let count = 0;

  for (const pt of coords) {
    if (Array.isArray(pt) && pt.length >= 2) {
      sumLon += pt[0];
      sumLat += pt[1];
      count++;
    }
  }

  if (count === 0) return null;
  return [sumLat / count, sumLon / count];
}

export class NearbyWildfiresWidget implements WidgetRenderer {
  public renderHtml(widget: ForecastWidgetConfig, ctx: WidgetRenderContext): string {
    const title = widget.title || 'Nearby Wildfires';
    const containerId = `poi-wildfires-card-${widget.id}`;

    if (cachedWildfiresGeoJson) {
      const fires = this.extractNearbyFires(cachedWildfiresGeoJson, ctx.lat, ctx.lon);
      return `
        <div id="${containerId}" class="metric-card" style="background: linear-gradient(145deg, #13151f 0%, #0d0e15 100%); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 14px; padding: 16px; box-shadow: 0 8px 24px rgba(0,0,0,0.3);">
          ${this.buildCardContent(title, fires)}
        </div>
      `;
    }

    return `
      <div id="${containerId}" class="metric-card" style="background: linear-gradient(145deg, #13151f 0%, #0d0e15 100%); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 14px; padding: 16px; box-shadow: 0 8px 24px rgba(0,0,0,0.3);">
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px;">
          <span style="font-size: 0.95rem; font-weight: 700; color: #fff;">🔥 ${title}</span>
          <span style="font-size: 0.7rem; padding: 2px 8px; background: rgba(239, 68, 68, 0.15); color: #f87171; border-radius: 999px; font-weight: 600;">Within 100 miles</span>
        </div>
        <div style="display: flex; align-items: center; justify-content: center; padding: 24px; color: #94a3b8; font-size: 0.85rem; gap: 8px;">
          <span>Scanning active fire perimeters...</span>
        </div>
      </div>
    `;
  }

  public init(widget: ForecastWidgetConfig, ctx: WidgetRenderContext): void {
    const containerId = `poi-wildfires-card-${widget.id}`;
    const containerEl = document.getElementById(containerId);
    if (!containerEl) return;

    const title = widget.title || 'Nearby Wildfires';

    const now = Date.now();
    if (cachedWildfiresGeoJson && now - lastWildfiresFetchTime < 10 * 60 * 1000) {
      const fires = this.extractNearbyFires(cachedWildfiresGeoJson, ctx.lat, ctx.lon);
      containerEl.innerHTML = this.buildCardContent(title, fires);
      this.bindActions(containerEl);
      return;
    }

    const state = store.getState();
    const manifest = state.manifest;
    let apiServer = manifest?.api_server;
    if (apiServer === undefined || apiServer === null) {
      apiServer = 'https://auth.skyskout.com/';
    } else if (apiServer === '') {
      const path = window.location.pathname;
      const dir = path.substring(0, path.lastIndexOf('/') + 1);
      apiServer = window.location.origin + dir;
    }
    if (!apiServer.endsWith('/')) apiServer += '/';
    const tileRoot = (manifest?.tile_base || 'tiles').replace(/^\/+|\/+$/g, '');
    const dataUrl = `${apiServer}${tileRoot}/observed/wildfires/wildfires.geojson`;

    const token = authService.getSessionToken();
    const fetchUrl = appendAuthToken(dataUrl);
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    fetch(fetchUrl, {
      headers,
      credentials: fetchUrl.includes('auth.skyskout.com') ? 'include' : 'same-origin'
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((geojson) => {
        cachedWildfiresGeoJson = geojson;
        lastWildfiresFetchTime = Date.now();
        const fires = this.extractNearbyFires(geojson, ctx.lat, ctx.lon);
        containerEl.innerHTML = this.buildCardContent(title, fires);
        this.bindActions(containerEl);
      })
      .catch((_err) => {
        containerEl.innerHTML = `
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px;">
            <span style="font-size: 0.95rem; font-weight: 700; color: #fff;">🔥 ${title}</span>
            <span style="font-size: 0.7rem; padding: 2px 8px; background: rgba(239, 68, 68, 0.15); color: #f87171; border-radius: 999px; font-weight: 600;">Within 100 miles</span>
          </div>
          <div style="text-align: center; padding: 18px; color: #94a3b8; font-size: 0.8rem;">
            Unable to load wildfire perimeters at this time.
            <div style="margin-top: 8px;">
              <button class="gw-wf-open-map-btn" style="background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.12); color: #e2e8f0; border-radius: 6px; padding: 6px 12px; font-size: 0.75rem; cursor: pointer;">
                Open Wildfire Map
              </button>
            </div>
          </div>
        `;
        this.bindActions(containerEl);
      });
  }

  private extractNearbyFires(geojson: any, userLat: number, userLon: number): ProcessedFire[] {
    if (!geojson || !Array.isArray(geojson.features)) return [];

    const firesByName = new Map<string, ProcessedFire>();

    for (const feat of geojson.features) {
      const props = feat.properties || {};
      const rawName = props.name || 'Wildfire';
      const cleanName = rawName.trim();
      const centroid = getCentroid(feat.geometry);
      if (!centroid) continue;

      const [fLat, fLon] = centroid;
      const dist = haversineDistanceMiles(userLat, userLon, fLat, fLon);

      // Filter: strictly within 100 miles
      if (dist > 100) continue;

      const bearing = getBearingLabel(userLat, userLon, fLat, fLon);
      const acres = typeof props.acres === 'number' ? props.acres : parseFloat(props.acres) || 0;
      const containment = props.containment !== undefined && props.containment !== null ? props.containment : 'Active';
      const startDate = props.start_date || '';
      const updatedDate = props.updated_date || '';
      const incidentType = props.incident_type || 'WF';

      const candidate: ProcessedFire = {
        name: cleanName,
        acres,
        containment,
        startDate,
        updatedDate,
        incidentType,
        lat: fLat,
        lon: fLon,
        distanceMiles: Math.round(dist * 10) / 10,
        bearing
      };

      const key = cleanName.toLowerCase();
      const existing = firesByName.get(key);
      if (!existing || candidate.distanceMiles < existing.distanceMiles) {
        firesByName.set(key, candidate);
      }
    }

    const sorted = Array.from(firesByName.values()).sort((a, b) => a.distanceMiles - b.distanceMiles);
    return sorted.slice(0, 5);
  }

  private buildCardContent(title: string, fires: ProcessedFire[]): string {
    const hasFires = fires.length > 0;
    const badgeText = hasFires ? `${fires.length} Active • Within 100 mi` : 'Within 100 miles';
    const badgeBg = hasFires ? 'rgba(239, 68, 68, 0.2)' : 'rgba(16, 185, 129, 0.15)';
    const badgeColor = hasFires ? '#f87171' : '#34d399';

    let listHtml = '';
    if (hasFires) {
      listHtml = `
        <div style="display: flex; flex-direction: column; gap: 10px; margin-top: 10px;">
          ${fires
            .map((f) => {
              const containmentBadge = this.formatContainmentBadge(f.containment);
              const acresStr = f.acres > 0 ? `${f.acres.toLocaleString()} acres` : 'Size unreported';
              const dateStr = f.startDate ? `Started ${f.startDate}` : '';

              return `
                <div style="background: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.06); border-radius: 10px; padding: 10px 12px; display: flex; flex-direction: column; gap: 6px;">
                  <div style="display: flex; justify-content: space-between; align-items: center; gap: 8px;">
                    <div style="font-size: 0.9rem; font-weight: 700; color: #f8fafc; display: flex; align-items: center; gap: 6px;">
                      <span>🔥</span>
                      <span>${this.escapeHtml(f.name)}</span>
                      ${f.incidentType === 'RX' ? '<span style="font-size: 0.65rem; padding: 1px 5px; background: rgba(59, 130, 246, 0.2); color: #60a5fa; border-radius: 4px;">Prescribed</span>' : ''}
                    </div>
                    <div style="font-size: 0.78rem; font-weight: 700; color: #fbbf24; background: rgba(245, 158, 11, 0.15); padding: 2px 7px; border-radius: 6px; white-space: nowrap;">
                      ${f.distanceMiles} mi ${f.bearing}
                    </div>
                  </div>

                  <div style="display: flex; justify-content: space-between; align-items: center; font-size: 0.75rem; color: #94a3b8; flex-wrap: wrap; gap: 6px;">
                    <div style="display: flex; gap: 10px; align-items: center;">
                      <span style="color: #e2e8f0; font-weight: 600;">${acresStr}</span>
                      ${dateStr ? `<span>•</span><span>${this.escapeHtml(dateStr)}</span>` : ''}
                    </div>
                    <div>${containmentBadge}</div>
                  </div>

                  <div style="display: flex; justify-content: flex-end; margin-top: 4px;">
                    <button class="gw-wf-fly-btn" data-lat="${f.lat}" data-lon="${f.lon}" style="background: rgba(239, 68, 68, 0.12); border: 1px solid rgba(239, 68, 68, 0.3); color: #fca5a5; font-size: 0.72rem; font-weight: 600; padding: 4px 10px; border-radius: 6px; cursor: pointer; display: flex; align-items: center; gap: 4px; transition: all 0.15s ease;">
                      <span>View on Wildfire Map</span>
                      <span>→</span>
                    </button>
                  </div>
                </div>
              `;
            })
            .join('')}
        </div>
      `;
    } else {
      listHtml = `
        <div style="background: rgba(16, 185, 129, 0.05); border: 1px solid rgba(16, 185, 129, 0.2); border-radius: 12px; padding: 18px 16px; margin-top: 8px; text-align: center; display: flex; flex-direction: column; align-items: center; gap: 6px;">
          <div style="font-size: 1.5rem;">🛡️</div>
          <div style="font-size: 0.95rem; font-weight: 700; color: #34d399;">No Active Wildfires Within 100 Miles</div>
          <div style="font-size: 0.75rem; color: #94a3b8; max-width: 320px; line-height: 1.4;">
            No reported active wildfire perimeters or thermal hotspots within 100 miles of this forecast location.
          </div>
          <button class="gw-wf-open-map-btn" style="margin-top: 8px; background: rgba(255, 255, 255, 0.06); border: 1px solid rgba(255, 255, 255, 0.12); color: #e2e8f0; border-radius: 6px; padding: 6px 14px; font-size: 0.75rem; font-weight: 600; cursor: pointer;">
            Explore Wildfire Map
          </button>
        </div>
      `;
    }

    return `
      <div style="display: flex; justify-content: space-between; align-items: center;">
        <div style="display: flex; align-items: center; gap: 8px;">
          <span style="font-size: 1.05rem;">🔥</span>
          <span style="font-size: 0.95rem; font-weight: 700; color: #fff;">${this.escapeHtml(title)}</span>
        </div>
        <span style="font-size: 0.7rem; padding: 3px 8px; background: ${badgeBg}; color: ${badgeColor}; border-radius: 999px; font-weight: 700;">
          ${badgeText}
        </span>
      </div>
      ${listHtml}
      ${
        hasFires
          ? `
        <div style="display: flex; justify-content: space-between; align-items: center; margin-top: 12px; padding-top: 8px; border-top: 1px solid rgba(255, 255, 255, 0.05); font-size: 0.72rem; color: #64748b;">
          <span>Source: US IRWIN & Canadian CWFIS</span>
          <button class="gw-wf-open-map-btn" style="background: none; border: none; color: #38bdf8; font-weight: 600; cursor: pointer; padding: 0;">
            Open Wildfire Layer →
          </button>
        </div>
      `
          : ''
      }
    `;
  }

  private formatContainmentBadge(val: string | number): string {
    if (typeof val === 'number') {
      const color = val >= 100 ? '#10b981' : val >= 50 ? '#34d399' : val > 0 ? '#fbbf24' : '#ef4444';
      const bg = val >= 100 ? 'rgba(16, 185, 129, 0.15)' : val >= 50 ? 'rgba(52, 211, 153, 0.15)' : val > 0 ? 'rgba(251, 191, 36, 0.15)' : 'rgba(239, 68, 68, 0.15)';
      return `<span style="padding: 2px 7px; background: ${bg}; color: ${color}; border-radius: 6px; font-weight: 700; font-size: 0.7rem;">${val}% Contained</span>`;
    }

    const str = String(val).trim();
    if (str.toLowerCase() === 'being held' || str.toLowerCase() === 'under control') {
      return `<span style="padding: 2px 7px; background: rgba(52, 211, 153, 0.15); color: #34d399; border-radius: 6px; font-weight: 700; font-size: 0.7rem;">${this.escapeHtml(str)}</span>`;
    }
    if (str.toLowerCase() === 'out of control') {
      return `<span style="padding: 2px 7px; background: rgba(239, 68, 68, 0.15); color: #f87171; border-radius: 6px; font-weight: 700; font-size: 0.7rem;">Out of Control</span>`;
    }
    return `<span style="padding: 2px 7px; background: rgba(245, 158, 11, 0.15); color: #fbbf24; border-radius: 6px; font-weight: 700; font-size: 0.7rem;">${this.escapeHtml(str)}</span>`;
  }

  private bindActions(container: HTMLElement): void {
    // Fly to specific fire
    container.querySelectorAll('.gw-wf-fly-btn').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const target = e.currentTarget as HTMLElement;
        const lat = parseFloat(target.getAttribute('data-lat') || '0');
        const lon = parseFloat(target.getAttribute('data-lon') || '0');

        store.update({ primaryProduct: 'wildfires', wildfiresEnabled: true });

        if (typeof (window as any).flyToWildfire === 'function') {
          (window as any).flyToWildfire(lat, lon);
        } else if ((window as any).scoutMap) {
          (window as any).scoutMap.flyTo({ center: [lon, lat], zoom: 9.5, essential: true });
        }

        poiModal.close();
      });
    });

    // Open wildfire map layer
    container.querySelectorAll('.gw-wf-open-map-btn').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        store.update({ primaryProduct: 'wildfires', wildfiresEnabled: true });
        poiModal.close();
      });
    });
  }

  private escapeHtml(str: string): string {
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }
}
